import type { EventTuple } from '@/events/types'
import type { AppEventLog } from '@/events/appLog'
import type { VoxelMap } from '@/voxelTypes'
import { toDocumentBase } from '@/storage/grid/documentBase'
import type { SnapshotMeta } from '@/document/model'

// Small, storage-agnostic helpers for log → tail/checkpoint calculations

export type CheckpointSummary = {
  index: number
  commitId: number
  base: VoxelMap
  meta: SnapshotMeta
}

type AppLogEntry = ReturnType<AppEventLog['getEntriesAfter']>[number]

function getEntriesToHead(appLog: AppEventLog): AppLogEntry[] {
  // getEntriesAfter(-1) returns entries [0..pointer]
  return appLog.getEntriesAfter(-1)
}

export function latestCheckpointSummary(appLog: AppEventLog): CheckpointSummary | null {
  const cp = appLog.getLatestCheckpoint()
  if (!cp) return null
  const base: VoxelMap = toDocumentBase(cp.base, undefined, cp.meta?.bounds)
  return {
    index: cp.index ?? -1,
    commitId: typeof cp.commitId === 'number' ? cp.commitId : 0,
    base,
    meta: cp.meta,
  }
}

// Storage records represent: checkpoint state at `sinceCommitId` plus every
// durable event after that commit up to the current log pointer.
export function extractTailSinceCommitId(appLog: AppEventLog, sinceCommitId: number | null | undefined): EventTuple[] {
  const entries = getEntriesToHead(appLog)
  let baselineIndex = -1
  if (typeof sinceCommitId === 'number' && sinceCommitId > 0) {
    const matchIdx = entries.findIndex((entry) => entry.commitId === sinceCommitId)
    if (matchIdx >= 0) baselineIndex = matchIdx
  }
  // sinceCommitId === 0 (sentinel) or not found => baselineIndex remains -1
  const slice = baselineIndex >= 0 ? entries.slice(baselineIndex + 1) : entries.slice()
  const tail: EventTuple[] = []
  for (const e of slice) {
    if (e.kind === 'event') tail.push(e.event as EventTuple)
    else if (e.kind === 'transaction') for (const ev of (e.events || [])) tail.push(ev as EventTuple)
  }
  return tail
}
